import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  DatabaseBackup,
  Play,
  Settings2,
  FolderOpen,
  Download,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  Save,
} from 'lucide-react';
import { apiService } from '@/core/api/service';
import { PageHeader } from '@/components/PageHeader';
import { SkeletonStats } from '@/components/SkeletonStats';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { BackupOverviewItem, BackupFile, BackupRun } from '@/core/models';

export const Route = createFileRoute('/backups')({
  head: () => ({
    meta: [{ title: 'Backup · Replya Internal Backoffice' }],
  }),
  component: BackupsPage,
});

const REFRESH_INTERVAL = 15000;

const CRON_PRESETS = [
  { label: 'Ogni giorno alle 03:00', value: '0 3 * * *' },
  { label: 'Ogni 6 ore', value: '0 */6 * * *' },
  { label: 'Ogni ora', value: '0 * * * *' },
  { label: 'Ogni domenica alle 03:00', value: '0 3 * * 0' },
];

function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
}

function StatusBadge({ status }: { status: 'running' | 'success' | 'error' }) {
  if (status === 'running') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> In corso
      </Badge>
    );
  }
  if (status === 'success') {
    return (
      <Badge variant="secondary" className="gap-1 text-emerald-500">
        <CheckCircle2 className="h-3 w-3" /> Completato
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1 text-destructive">
      <XCircle className="h-3 w-3" /> Errore
    </Badge>
  );
}

function BackupsPage() {
  const queryClient = useQueryClient();
  const [configTenant, setConfigTenant] = useState<BackupOverviewItem | null>(null);
  const [filesTenant, setFilesTenant] = useState<BackupOverviewItem | null>(null);

  const overviewQuery = useQuery<BackupOverviewItem[], Error>({
    queryKey: ['backupsOverview'],
    queryFn: () => apiService.getBackupsOverview(),
    refetchInterval: REFRESH_INTERVAL,
  });

  const triggerMutation = useMutation({
    mutationFn: (tenantId: string) => apiService.triggerBackup(tenantId),
    onSuccess: (_data, tenantId) => {
      toast.success(`Backup avviato per "${tenantId}"`);
      queryClient.invalidateQueries({ queryKey: ['backupsOverview'] });
    },
    onError: (err: Error) => toast.error(`Avvio backup fallito: ${err.message}`),
  });

  const { data, isLoading, isError, error, refetch } = overviewQuery;

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Backup"
        subtitle="Backup automatici e manuali dei database tenant, con retention configurabile su S3"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
          className="rounded-xl"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </PageHeader>

      {isLoading && <SkeletonStats count={3} />}

      {isError && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-5 py-4 text-sm text-destructive flex items-center gap-3">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold">Errore durante il caricamento</p>
            <p className="text-destructive/80 break-words">{error.message}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="rounded-xl shrink-0">
            Riprova
          </Button>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="grid gap-4">
          {(data ?? []).length === 0 && (
            <Card className="rounded-2xl border bg-surface">
              <CardContent className="p-6 text-sm text-muted-foreground">
                Nessun tenant registrato.
              </CardContent>
            </Card>
          )}
          {(data ?? []).map((item) => (
            <Card key={item.tenantId} className="rounded-2xl border bg-surface">
              <CardContent className="p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <DatabaseBackup className="h-4 w-4 text-brand shrink-0" />
                      <span className="font-semibold truncate">{item.name || item.tenantId}</span>
                      <span className="text-xs text-muted-foreground">({item.tenantId})</span>
                      <Badge variant={item.enabled ? 'default' : 'secondary'} className="ml-1">
                        {item.enabled ? 'Cron attivo' : 'Cron disattivo'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>
                        Schedule: <code className="rounded bg-surface-2 px-1.5 py-0.5">{item.cronExpression}</code>
                      </span>
                      <span>Retention: {item.retentionDays} gg</span>
                    </div>
                    {item.lastRun ? (
                      <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
                        <StatusBadge status={item.lastRun.status} />
                        <span className="text-muted-foreground">
                          {item.lastRun.triggeredBy === 'cron' ? 'automatico' : 'manuale'} · {formatDateTime(item.lastRun.startedAt)}
                        </span>
                        {item.lastRun.sizeBytes !== undefined && (
                          <span className="text-muted-foreground">· {formatBytes(item.lastRun.sizeBytes)}</span>
                        )}
                        {item.lastRun.error && (
                          <span className="text-destructive break-words">· {item.lastRun.error}</span>
                        )}
                      </div>
                    ) : (
                      <p className="pt-1 text-sm text-muted-foreground">Nessun backup eseguito finora.</p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 md:shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => triggerMutation.mutate(item.tenantId)}
                      disabled={triggerMutation.isPending || item.lastRun?.status === 'running'}
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Esegui ora
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => setConfigTenant(item)}
                    >
                      <Settings2 className="mr-2 h-4 w-4" />
                      Configura
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => setFilesTenant(item)}
                    >
                      <FolderOpen className="mr-2 h-4 w-4" />
                      File
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfigDialog tenant={configTenant} onClose={() => setConfigTenant(null)} />
      <FilesDialog tenant={filesTenant} onClose={() => setFilesTenant(null)} />
    </div>
  );
}

function ConfigDialog({ tenant, onClose }: { tenant: BackupOverviewItem | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [cronExpression, setCronExpression] = useState('0 3 * * *');
  const [retentionDays, setRetentionDays] = useState(30);

  useEffect(() => {
    if (tenant) {
      setEnabled(tenant.enabled);
      setCronExpression(tenant.cronExpression);
      setRetentionDays(tenant.retentionDays);
    }
  }, [tenant]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!tenant) throw new Error('Nessun tenant selezionato');
      return apiService.updateBackupConfig(tenant.tenantId, { enabled, cronExpression, retentionDays });
    },
    onSuccess: () => {
      toast.success('Configurazione backup salvata');
      queryClient.invalidateQueries({ queryKey: ['backupsOverview'] });
      onClose();
    },
    onError: (err: Error) => toast.error(`Salvataggio fallito: ${err.message}`),
  });

  return (
    <Dialog open={!!tenant} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configura backup — {tenant?.name || tenant?.tenantId}</DialogTitle>
          <DialogDescription>
            Imposta la schedulazione automatica e la retention dei backup su S3.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch id="backup-enabled" checked={enabled} onCheckedChange={setEnabled} />
            <Label htmlFor="backup-enabled" className="text-sm">Backup automatico attivo</Label>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Espressione cron</Label>
            <Input
              value={cronExpression}
              onChange={(e) => setCronExpression(e.target.value)}
              placeholder="0 3 * * *"
              className="rounded-xl bg-surface-2 border-border font-mono"
            />
            <div className="flex flex-wrap gap-2">
              {CRON_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setCronExpression(preset.value)}
                  className="rounded-lg bg-surface-2 px-2 py-1 text-xs text-muted-foreground hover:bg-surface-3 hover:text-foreground"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Retention (giorni)</Label>
            <Input
              type="number"
              min={1}
              value={retentionDays}
              onChange={(e) => setRetentionDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="rounded-xl bg-surface-2 border-border w-32"
            />
            <p className="text-xs text-muted-foreground">
              I backup più vecchi di questo numero di giorni vengono eliminati automaticamente da S3 dopo ogni run.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">
            Annulla
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
          >
            <Save className="mr-2 h-4 w-4" />
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilesDialog({ tenant, onClose }: { tenant: BackupOverviewItem | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('files');
  const [fileToDelete, setFileToDelete] = useState<BackupFile | null>(null);
  const tenantId = tenant?.tenantId;

  const filesQuery = useQuery<BackupFile[], Error>({
    queryKey: ['backupFiles', tenantId],
    queryFn: () => apiService.getBackupFiles(tenantId as string),
    enabled: !!tenantId,
  });

  const runsQuery = useQuery({
    queryKey: ['backupRuns', tenantId],
    queryFn: () => apiService.getBackupRuns(tenantId as string, { limit: 20 }),
    enabled: !!tenantId,
  });

  const deleteMutation = useMutation({
    mutationFn: (file: BackupFile) => apiService.deleteBackupFile(tenantId as string, file.filename),
    onSuccess: (_data, file) => {
      toast.success(`Backup "${file.filename}" eliminato`);
      queryClient.invalidateQueries({ queryKey: ['backupFiles', tenantId] });
      setFileToDelete(null);
    },
    onError: (err: Error) => toast.error(`Eliminazione fallita: ${err.message}`),
  });

  const handleDownload = async (file: BackupFile) => {
    try {
      const url = await apiService.getBackupDownloadUrl(tenantId as string, file.filename);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(`Download fallito: ${(err as Error).message}`);
    }
  };

  return (
      <Dialog open={!!tenant} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Backup — {tenant?.name || tenant?.tenantId}</DialogTitle>
            <DialogDescription>File salvati su S3 e storico delle esecuzioni.</DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="rounded-xl bg-surface-2">
              <TabsTrigger value="files" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
                File
              </TabsTrigger>
              <TabsTrigger value="runs" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
                Storico esecuzioni
              </TabsTrigger>
            </TabsList>

            <TabsContent value="files" className="mt-4 max-h-96 space-y-2 overflow-auto">
              {filesQuery.isLoading && <SkeletonStats count={2} />}
              {filesQuery.isError && (
                <p className="text-sm text-destructive">{filesQuery.error.message}</p>
              )}
              {!filesQuery.isLoading && (filesQuery.data ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground">Nessun file di backup presente.</p>
              )}
              {(filesQuery.data ?? []).map((file) => (
                <div
                  key={file.key}
                  className="flex items-center justify-between gap-3 rounded-xl bg-surface-2 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{file.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(file.size)} · {formatDateTime(file.lastModified)}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-lg"
                      onClick={() => handleDownload(file)}
                      title="Scarica"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-lg text-destructive hover:text-destructive"
                      onClick={() => setFileToDelete(file)}
                      title="Elimina"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="runs" className="mt-4 max-h-96 space-y-2 overflow-auto">
              {runsQuery.isLoading && <SkeletonStats count={2} />}
              {runsQuery.isError && (
                <p className="text-sm text-destructive">{(runsQuery.error as Error).message}</p>
              )}
              {!runsQuery.isLoading && (runsQuery.data?.items ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground">Nessuna esecuzione registrata.</p>
              )}
              {(runsQuery.data?.items ?? []).map((run: BackupRun) => (
                <div key={run.id} className="rounded-xl bg-surface-2 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={run.status} />
                    <span className="text-xs text-muted-foreground">
                      {run.triggeredBy === 'cron' ? 'automatico' : 'manuale'} · {formatDateTime(run.startedAt)}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    {run.durationMs !== undefined && <span>{(run.durationMs / 1000).toFixed(1)}s</span>}
                    {run.sizeBytes !== undefined && <span>{formatBytes(run.sizeBytes)}</span>}
                    {run.collectionsCount !== undefined && <span>{run.collectionsCount} collection</span>}
                    {run.documentsCount !== undefined && <span>{run.documentsCount} documenti</span>}
                    {!!run.deletedByRetention && <span>{run.deletedByRetention} rimossi (retention)</span>}
                  </div>
                  {run.error && <p className="mt-1 text-xs text-destructive break-words">{run.error}</p>}
                </div>
              ))}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} className="rounded-xl">
              Chiudi
            </Button>
          </DialogFooter>

          {/* Annidato dentro il Dialog (non come sibling) per non far scattare la
              chiusura del Dialog esterno: Radix tratta il click sull'AlertDialog
              come "outside click" se i due componenti non condividono l'albero React. */}
          <AlertDialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare questo backup?</AlertDialogTitle>
                <AlertDialogDescription>
                  Il file "{fileToDelete?.filename}" verrà eliminato definitivamente da S3. Operazione non reversibile.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => fileToDelete && deleteMutation.mutate(fileToDelete)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Elimina
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogContent>
      </Dialog>
  );
}
